import { useTeamStore } from '@/stores/team'
import { useAuthStore } from '@/stores/auth'
import { useUIStore } from '@/stores/ui'
import { getTrialStatus, getPlanKind, type TrialStatus, type PlanKind } from '@/lib/utils'

export function useTrialStatus(): TrialStatus | null {
  const team = useTeamStore((s) => s.team)
  return getTrialStatus(team?.planName, team?.planExpiresAt)
}

export function usePlanKind(): PlanKind | null {
  const team = useTeamStore((s) => s.team)
  return getPlanKind(team?.planName)
}

// TeamAdmin.role === 'internal' or 'salesRep' — Rolla staff managing a team on the customer's
// behalf, not the customer's own admin. Global admins (Profile.globalAdmin) always pass too —
// isGlobalAdmin and isInternalAdmin are independent flags (a global admin has no guaranteed
// team_admin row on every team), but internal-admin-gated UI is meant to be a subset of what a
// global admin can see, not a sibling of it.
export function useIsInternalAdmin(): boolean {
  const selectedTeamId = useAuthStore((s) => s.selectedTeamId)
  const teamAdmins = useAuthStore((s) => s.teamAdmins)
  const isGlobalAdmin = useAuthStore((s) => s.isGlobalAdmin)
  const role = teamAdmins.find((ta) => ta.teamId === selectedTeamId)?.role
  return isGlobalAdmin || role === 'internal' || role === 'salesRep'
}

// Unlike useIsInternalAdmin, not scoped to the currently selected team — true if the caller has
// an internal role on ANY team. Used to gate features that aren't about the team they're
// currently viewing (e.g. standing up a brand-new demo account before one exists to select).
export function useHasInternalAdminRole(): boolean {
  const teamAdmins = useAuthStore((s) => s.teamAdmins)
  const isGlobalAdmin = useAuthStore((s) => s.isGlobalAdmin)
  return isGlobalAdmin || teamAdmins.some((ta) => ta.role === 'internal' || ta.role === 'salesRep')
}

export function useIsAccountLocked(): boolean {
  const isTrialExpired = useTrialStatus() === 'trialExpired'
  const isInternalAdmin = useIsInternalAdmin()
  const previewAsRegularAdmin = useUIStore((s) => s.previewAsRegularAdmin)
  // Internal admins aren't locked out by default — they can flip previewAsRegularAdmin (sidebar
  // toggle) to see the locked-out experience a regular admin on this team would get.
  if (isInternalAdmin && !previewAsRegularAdmin) return false
  return isTrialExpired
}
